import {
  ResponseLabelCheck,
  ResponseTypeEnum,
  TemplateNodeSchema,
} from '@ulysses-inc/harami_api_client'
import { Button, Drawer, Form, Row } from 'antd'
import { FC, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSiblingQuestions } from 'src/features/templateEdit/common/useSiblingQuestions'
import { updateTemplateNode } from 'src/state/ducks/templates/actions'
import styled from 'styled-components'
import { SelectLabelMaster } from './SelectLabelMaster'
import { SelectQuestionWithToggle } from './SelectQuestionWithToggle'

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
`

type FormData = {
  labelMasterId?: string
  baseTimeReferenceDatetimeQuestionNodeUUID?: string
  referenceTextQuestionNodeUUID?: string
}

const initializeFormData = (node: TemplateNodeSchema): FormData => {
  const responseLabelCheck = node.question?.responseLabelChecks?.[0]

  return {
    labelMasterId: responseLabelCheck?.labelInspectionId,
    baseTimeReferenceDatetimeQuestionNodeUUID:
      responseLabelCheck?.baseTimeReferenceDatetimeQuestionNodeUUID,
    referenceTextQuestionNodeUUID:
      responseLabelCheck?.referenceTextQuestionNodeUUID,
  }
}

type Props = {
  /** 編集対象の質問ノード */
  node: TemplateNodeSchema
  isOpen: boolean
  onClose: () => void
}

const EditResponseLabelCheckDrawerContainer: FC<Props> = ({
  node,
  onClose,
  isOpen,
}) => {
  const dispatch = useDispatch()

  const datetimeNodes = useSiblingQuestions(node.id, ResponseTypeEnum.DATETIME)
  const textNodes = useSiblingQuestions(node.id, ResponseTypeEnum.TEXT)

  const changeResponseLabelCheck = (
    node: TemplateNodeSchema,
    newResponse: ResponseLabelCheck,
  ) => {
    dispatch(
      updateTemplateNode(node.id, {
        ...node,
        question: {
          ...node.question,
          responseLabelChecks: [newResponse],
        },
      }),
    )
  }
  const onCloseOrCancel = () => {
    onClose()
  }

  const [formData, setFormData] = useState<FormData>(initializeFormData(node))

  const onSubmit = () => {
    changeResponseLabelCheck(node, {
      labelInspectionId: formData.labelMasterId ?? undefined,
      baseTimeReferenceDatetimeQuestionNodeUUID:
        formData.baseTimeReferenceDatetimeQuestionNodeUUID ?? undefined,
      referenceTextQuestionNodeUUID:
        formData.referenceTextQuestionNodeUUID ?? undefined,
    })
    onClose()
  }

  return (
    <Drawer
      title="設定"
      maskClosable={false}
      placement="right"
      onClose={onCloseOrCancel}
      open={isOpen}
      width="600"
    >
      <Form layout="vertical">
        <SelectLabelMaster
          labelMasterId={formData.labelMasterId}
          setLabelMasterId={value =>
            setFormData({ ...formData, labelMasterId: value })
          }
        />

        <SelectQuestionWithToggle
          nodes={datetimeNodes}
          value={formData.baseTimeReferenceDatetimeQuestionNodeUUID}
          onChange={value =>
            setFormData({
              ...formData,
              baseTimeReferenceDatetimeQuestionNodeUUID: value,
            })
          }
          toggleLabel="日付質問を基準時間にする"
          autocompleteLabel="基準にする日時質問"
          autocompletePlaceholder="日時質問を選択してください"
          autocompleteDescription={
            <>
              ラベルマスタの設定で基準の日付を求める箇所がある場合、この質問を基準日時にします。
              <br />
              指定する日時質問フォーマットは「日時」である必要があります。
            </>
          }
        />
        <SelectQuestionWithToggle
          nodes={textNodes}
          value={formData.referenceTextQuestionNodeUUID}
          onChange={value =>
            setFormData({
              ...formData,
              referenceTextQuestionNodeUUID: value,
            })
          }
          toggleLabel="ラベルマスタの正解値を指定した質問の回答にする"
          autocompleteLabel="正解値にするテキスト質問"
          autocompletePlaceholder="テキスト質問を選択してください"
        />
        <Row style={{ justifyContent: 'flex-end', marginTop: '24px' }}>
          <ButtonGroup>
            <Button type="link" onClick={onCloseOrCancel}>
              キャンセル
            </Button>
            <Button type="primary" onClick={onSubmit}>
              保存
            </Button>
          </ButtonGroup>
        </Row>
      </Form>
    </Drawer>
  )
}

export default EditResponseLabelCheckDrawerContainer
